import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import { StyleSheet, View, type ViewProps } from "react-native";
import { ComboBox, type ComboBoxProps, type ItemLike, type ValueLike } from "~/components/form/combo-box/ComboBox";
import { NumberInput, type NumberInputProps, type NumericSetter } from "~/components/form/NumberInput";
import { setWidthOnLayout } from "~/util/layout";
import type { NumberFormatOptions, OptionalNumeric } from "~/util/string";

export type NumberComboProps<TValue extends ValueLike, TItem extends ItemLike<TValue>> = {
    number: OptionalNumeric;
    setNumber: NumericSetter;
    combo: TValue;
    setCombo: ((v: TValue) => void) | Dispatch<SetStateAction<TValue>>;
    comboWidth?: number;
    readonly format?: NumberFormatOptions;
    comboProps: Pick<
        ComboBoxProps<TValue, TItem>,
        "items" | "mapItems" | "itemMap" | "onAddItem" | "addable" | "searchable" | "placeholder" | "label"
    >;
    containerProps?: ViewProps;
} & Omit<NumberInputProps, "value" | "onChangeText">;

const COLUMN_GAP = 12;
const ROW_GAP = 8;

export const NumberCombo = <TValue extends ValueLike = ValueLike, TItem extends ItemLike<TValue> = ItemLike<TValue>>({
    number,
    setNumber,
    combo,
    setCombo,
    comboProps,
    disabled,
    containerProps,
    comboWidth = 90,
    ...inputProps
}: NumberComboProps<TValue, TItem>) => {
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth, containerProps?.onLayout);
    const styles = useMemo(() => {
        const field_width = Math.floor(width - (COLUMN_GAP + comboWidth));

        if (field_width >= 100) {
            return StyleSheet.create({
                container: {
                    flexDirection: "row",
                    gap: COLUMN_GAP,
                    alignItems: "flex-end",
                },
                input: {
                    width: field_width,
                },
                combo: {
                    width: comboWidth,
                    flexShrink: 0,
                },
            });
        } else {
            return StyleSheet.create({
                container: {
                    flexDirection: "column",
                    gap: ROW_GAP,
                    alignItems: "stretch",
                },
                input: {
                    width: width,
                },
                combo: {
                    width: width,
                },
            });
        }
    }, [width, comboWidth]);

    return (
        <View {...containerProps} style={[styles.container, containerProps?.style]} onLayout={onLayout}>
            <NumberInput
                {...inputProps}
                disabled={disabled}
                value={number}
                onChangeText={setNumber}
                containerStyle={styles.input}
            />
            <ComboBox<TValue, TItem>
                {...comboProps}
                disabled={disabled}
                value={combo}
                onItemSelected={setCombo}
                style={styles.combo}
                hideLabel
            />
        </View>
    );
};
